import { Text, View } from '@react-pdf/renderer';
import { format } from 'date-fns';

import { ReachProOutlineLogoPDF } from '../RetailInvoice/ReachProOutlineLogoPDF';
import { formatAmountShort } from '../RetailInvoice/RetailInvoicePDF.utils';
import { styles } from './PurchaseOrderPDF.css';
import {
  PurchaseOrderEventGroup,
  PurchaseOrderPDFProps,
  PurchaseOrderTicketRow,
} from './PurchaseOrderPDF.types';

// ────────────────────────────────────────────────────────────
// POHeaderSection
// ────────────────────────────────────────────────────────────

type POHeaderSectionProps = Pick<PurchaseOrderPDFProps, 'poStatus' | 'isPaid'>;

export const POHeaderSection = ({ poStatus, isPaid }: POHeaderSectionProps) => {
  const statusStyle =
    poStatus === 'Active' ? styles.poStatusActive : styles.poStatusCancelled;

  return (
    <View style={styles.headerSection}>
      {/* Left: logo placeholder + title + status */}
      <View style={styles.headerLeft}>
        <View style={styles.logoPlaceholder} />
        <Text style={styles.poTitle}>Purchase Order</Text>
        <Text style={statusStyle}>{poStatus}</Text>
      </View>

      {/* Right: PAID badge if applicable */}
      <View style={styles.headerRight}>
        {isPaid && <Text style={styles.paidBadge}>PAID</Text>}
      </View>
    </View>
  );
};

// ────────────────────────────────────────────────────────────
// POMetadataRow
// ────────────────────────────────────────────────────────────

type POMetadataRowProps = Pick<
  PurchaseOrderPDFProps,
  'poDate' | 'poNumber' | 'externalPoNumber'
>;

export const POMetadataRow = ({
  poDate,
  poNumber,
  externalPoNumber,
}: POMetadataRowProps) => (
  <View style={styles.metadataTable}>
    <View style={styles.metadataCell}>
      <Text style={styles.metadataHeaderText}>Date</Text>
      <Text style={styles.metadataValueText}>{formatPODate(poDate)}</Text>
    </View>
    <View style={styles.metadataCell}>
      <Text style={styles.metadataHeaderText}>P.O No.</Text>
      <Text style={styles.metadataValueText}>{poNumber}</Text>
    </View>
    <View style={styles.metadataCellLast}>
      <Text style={styles.metadataHeaderText}>External PO#</Text>
      <Text style={styles.metadataValueText}>{externalPoNumber || 'n/a'}</Text>
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// POAddressSection
// ────────────────────────────────────────────────────────────

type POAddressSectionProps = Pick<
  PurchaseOrderPDFProps,
  | 'vendorName'
  | 'vendorAddress'
  | 'shipToName'
  | 'shipToAddress'
  | 'shipToPhone'
>;

export const POAddressSection = ({
  vendorName,
  vendorAddress,
  shipToName,
  shipToAddress,
  shipToPhone,
}: POAddressSectionProps) => (
  <View style={styles.addressSection}>
    {/* Vendor */}
    <View style={styles.addressColumn}>
      <Text style={styles.addressSectionTitle}>Vendor</Text>
      {vendorName ? (
        <Text style={styles.addressName}>{vendorName}</Text>
      ) : (
        <Text style={styles.addressName}>—</Text>
      )}
      {vendorAddress ? (
        <Text style={styles.addressDetail}>{vendorAddress}</Text>
      ) : null}
    </View>

    {/* Ship To */}
    <View style={styles.addressColumnRight}>
      <Text style={styles.addressSectionTitle}>Ship To</Text>
      {shipToName ? (
        <Text style={styles.addressName}>{shipToName}</Text>
      ) : (
        <Text style={styles.addressName}>—</Text>
      )}
      {shipToAddress ? (
        <Text style={styles.addressDetail}>{shipToAddress}</Text>
      ) : null}
      {shipToPhone ? (
        <Text style={styles.addressDetail}>{shipToPhone}</Text>
      ) : null}
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// PODetailInfoSection
// ────────────────────────────────────────────────────────────

type PODetailInfoSectionProps = Pick<
  PurchaseOrderPDFProps,
  | 'vendorId'
  | 'vendorContact'
  | 'vendorPhone'
  | 'vendorEmail'
  | 'buyerRep'
  | 'buyerRepPhone'
  | 'paymentMethod'
  | 'paymentTerm'
>;

export const PODetailInfoSection = ({
  vendorId,
  vendorContact,
  vendorPhone,
  vendorEmail,
  buyerRep,
  buyerRepPhone,
  paymentMethod,
  paymentTerm,
}: PODetailInfoSectionProps) => (
  <View style={styles.detailInfoSection}>
    {/* Left column: Vendor details */}
    <View style={styles.detailInfoColumn}>
      <View style={styles.detailInfoRow}>
        <Text style={styles.detailInfoLabel}>Vendor ID #:</Text>
        <Text style={styles.detailInfoValue}>{vendorId ?? 'n/a'}</Text>
      </View>
      <View style={styles.detailInfoRow}>
        <Text style={styles.detailInfoLabel}>Vendor Contact:</Text>
        <Text style={styles.detailInfoValue}>{vendorContact ?? 'n/a'}</Text>
      </View>
      <View style={styles.detailInfoRow}>
        <Text style={styles.detailInfoLabel}>Phone:</Text>
        <Text style={styles.detailInfoValue}>{vendorPhone ?? 'n/a'}</Text>
      </View>
      <View style={styles.detailInfoRow}>
        <Text style={styles.detailInfoLabel}>Email:</Text>
        <Text style={styles.detailInfoValue}>{vendorEmail ?? 'n/a'}</Text>
      </View>
    </View>

    {/* Right column: Buyer/Payment details */}
    <View style={styles.detailInfoColumnLast}>
      <View style={styles.detailInfoRow}>
        <Text style={styles.detailInfoLabel}>Buyer's Rep:</Text>
        <Text style={styles.detailInfoValue}>{buyerRep ?? 'n/a'}</Text>
      </View>
      <View style={styles.detailInfoRow}>
        <Text style={styles.detailInfoLabel}>Phone:</Text>
        <Text style={styles.detailInfoValue}>{buyerRepPhone ?? 'n/a'}</Text>
      </View>
      <View style={styles.detailInfoRow}>
        <Text style={styles.detailInfoLabel}>Payment Method:</Text>
        <Text style={styles.detailInfoValue}>{paymentMethod ?? 'n/a'}</Text>
      </View>
      {paymentTerm ? (
        <View style={styles.detailInfoRow}>
          <Text style={styles.detailInfoLabel}>Payment Term:</Text>
          <Text style={styles.detailInfoValue}>{paymentTerm}</Text>
        </View>
      ) : null}
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// POTicketTable (per event)
// ────────────────────────────────────────────────────────────

const POTicketTable = ({
  ticketRows,
  eventTotal,
}: {
  ticketRows: PurchaseOrderTicketRow[];
  eventTotal: number;
}) => (
  <View style={styles.ticketTable}>
    {/* Table header */}
    <View style={styles.ticketTableHeaderRow}>
      <View style={styles.colQty}>
        <Text style={styles.ticketTableHeaderCell}>Qty</Text>
      </View>
      <View style={styles.colSection}>
        <Text style={styles.ticketTableHeaderCell}>Section</Text>
      </View>
      <View style={styles.colRow}>
        <Text style={styles.ticketTableHeaderCell}>Row</Text>
      </View>
      <View style={styles.colSeats}>
        <Text style={styles.ticketTableHeaderCell}>Seats</Text>
      </View>
      <View style={styles.colFace}>
        <Text style={styles.ticketTableHeaderCellRight}>Face Value</Text>
      </View>
      <View style={styles.colCost}>
        <Text style={styles.ticketTableHeaderCellRight}>Cost</Text>
      </View>
      <View style={styles.colFLC}>
        <Text style={styles.ticketTableHeaderCellRight}>Fully Loaded Cost</Text>
      </View>
      <View style={styles.colNotes}>
        <Text style={styles.ticketTableHeaderCell}>Notes</Text>
      </View>
      <View style={styles.colTotal}>
        <Text style={styles.ticketTableHeaderCellRight}>Total</Text>
      </View>
    </View>

    {/* Data rows */}
    {ticketRows.map((row, index) => {
      const seatRange =
        row.seatFrom && row.seatTo
          ? `${row.seatFrom} - ${row.seatTo}`
          : (row.seatFrom ?? '—');
      const rowTotal = (row.cost ?? 0) * row.qty;

      return (
        <View
          key={index}
          style={[
            styles.ticketTableRow,
            index % 2 === 1 ? styles.ticketTableRowAlt : {},
          ]}
        >
          <View style={styles.colQty}>
            <Text style={styles.ticketTableCell}>{row.qty}</Text>
          </View>
          <View style={styles.colSection}>
            <Text style={styles.ticketTableCell}>{row.section ?? '—'}</Text>
          </View>
          <View style={styles.colRow}>
            <Text style={styles.ticketTableCell}>{row.row ?? '—'}</Text>
          </View>
          <View style={styles.colSeats}>
            <Text style={styles.ticketTableCell}>{seatRange}</Text>
          </View>
          <View style={styles.colFace}>
            <Text style={styles.ticketTableCellRight}>
              {row.faceValue != null ? formatAmountShort(row.faceValue) : '—'}
            </Text>
          </View>
          <View style={styles.colCost}>
            <Text style={styles.ticketTableCellRight}>
              {row.cost != null ? formatAmountShort(row.cost) : '—'}
            </Text>
          </View>
          <View style={styles.colFLC}>
            <Text style={styles.ticketTableCellRight}>
              {row.fullyLoadedCost != null
                ? formatAmountShort(row.fullyLoadedCost)
                : '—'}
            </Text>
          </View>
          <View style={styles.colNotes}>
            <Text style={styles.ticketTableCellNotes}>
              {row.listingNotes ?? ''}
            </Text>
          </View>
          <View style={styles.colTotal}>
            <Text style={styles.ticketTableCellRight}>
              {formatAmountShort(rowTotal)}
            </Text>
          </View>
        </View>
      );
    })}

    {/* Event total row */}
    <View style={styles.eventTotalRow}>
      <Text style={styles.eventTotalLabel}>Event Total:</Text>
      <Text style={styles.eventTotalValue}>
        {formatAmountShort(eventTotal)}
      </Text>
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// POEventGroups
// ────────────────────────────────────────────────────────────

type POEventGroupsProps = Pick<
  PurchaseOrderPDFProps,
  'eventGroups' | 'currency'
>;

export const POEventGroups = ({ eventGroups }: POEventGroupsProps) => (
  <View>
    {eventGroups.map((group, index) => (
      <View key={index} style={styles.eventGroupSection} wrap={false}>
        {/* Event header bar */}
        <View style={styles.eventHeaderBar}>
          <Text style={styles.eventHeaderText}>
            {buildEventHeaderText(group)}
          </Text>
        </View>

        {/* Ticket rows table */}
        <POTicketTable
          ticketRows={group.ticketRows}
          eventTotal={group.eventTotal}
        />
      </View>
    ))}
  </View>
);

const buildEventHeaderText = (group: PurchaseOrderEventGroup): string => {
  const parts: string[] = [group.eventName];
  if (group.eventDate) {
    parts.push(formatPODate(group.eventDate, 'MMM d yyyy h:mmaaa'));
  }
  if (group.venueName) {
    parts.push(`at ${group.venueName}`);
  }
  const location = [group.eventCity, group.eventState]
    .filter(Boolean)
    .join(', ');
  if (location) {
    parts.push(location);
  }
  return parts.join(' ');
};

const formatPODate = (
  date: string | Date | undefined | null,
  formatStr?: string
): string => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), formatStr ?? 'MM/dd/yyyy h:mm a');
  } catch {
    return String(date);
  }
};

// ────────────────────────────────────────────────────────────
// POPaymentAndSummary
// ────────────────────────────────────────────────────────────

type POPaymentAndSummaryProps = Pick<
  PurchaseOrderPDFProps,
  | 'payments'
  | 'totalTickets'
  | 'totalTicketCost'
  | 'totalTaxes'
  | 'totalExpenses'
  | 'totalShipping'
  | 'poTotal'
  | 'totalPaid'
  | 'balanceDue'
  | 'currency'
>;

export const POPaymentAndSummary = ({
  payments,
  totalTickets,
  totalTicketCost,
  totalTaxes,
  totalExpenses,
  totalShipping,
  poTotal,
  totalPaid,
  balanceDue,
}: POPaymentAndSummaryProps) => (
  <View style={styles.bottomSection} wrap={false}>
    {/* Left: Payment Information */}
    <View style={styles.bottomLeft}>
      <Text style={styles.sectionTitle}>Payment Information</Text>
      {payments.map((pmt, index) => (
        <View key={index} style={styles.paymentRow}>
          <Text style={styles.paymentMethod}>{pmt.method}</Text>
          <Text style={styles.paymentDate}>
            {pmt.date ? formatPODate(pmt.date) : ''}
          </Text>
          <Text style={styles.paymentAmount}>
            {formatAmountShort(pmt.amount)}
          </Text>
        </View>
      ))}
    </View>

    {/* Right: Summary */}
    <View style={styles.bottomRight}>
      <Text style={styles.sectionTitle}>Summary</Text>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Tickets:</Text>
        <Text style={styles.summaryValue}>{totalTickets}</Text>
      </View>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Total Ticket Cost:</Text>
        <Text style={styles.summaryValue}>
          {formatAmountShort(totalTicketCost)}
        </Text>
      </View>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Taxes:</Text>
        <Text style={styles.summaryValue}>{formatAmountShort(totalTaxes)}</Text>
      </View>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Expenses:</Text>
        <Text style={styles.summaryValue}>
          {formatAmountShort(totalExpenses)}
        </Text>
      </View>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Shipping:</Text>
        <Text style={styles.summaryValue}>
          {formatAmountShort(totalShipping)}
        </Text>
      </View>
      <View style={styles.summaryRowHighlight}>
        <Text style={styles.summaryLabelBold}>PO Total:</Text>
        <Text style={styles.summaryValueBold}>
          {formatAmountShort(poTotal)}
        </Text>
      </View>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Total Paid:</Text>
        <Text style={styles.summaryValue}>{formatAmountShort(totalPaid)}</Text>
      </View>
      <View style={styles.summaryRowHighlight}>
        <Text style={styles.summaryLabelBold}>Balance Due:</Text>
        <Text style={styles.summaryValueBold}>
          {formatAmountShort(balanceDue)}
        </Text>
      </View>
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// PONotesSection
// ────────────────────────────────────────────────────────────

type PONotesSectionProps = Pick<
  PurchaseOrderPDFProps,
  | 'purchaseOrderNotes'
  | 'shipMethod'
  | 'shipType'
  | 'trackingNumber'
  | 'estimatedArrival'
  | 'shippingNotes'
>;

export const PONotesSection = ({
  purchaseOrderNotes,
  shipMethod,
  shipType,
  trackingNumber,
  estimatedArrival,
  shippingNotes,
}: PONotesSectionProps) => (
  <View>
    {/* Purchase Order Notes */}
    {purchaseOrderNotes ? (
      <View style={styles.notesSection}>
        <Text style={styles.sectionTitle}>Purchase Order Notes</Text>
        <Text style={styles.notesText}>{purchaseOrderNotes}</Text>
      </View>
    ) : null}

    {/* Shipping Notes */}
    {shipMethod ||
    shipType ||
    trackingNumber ||
    estimatedArrival ||
    shippingNotes ? (
      <View style={styles.shippingSection}>
        <Text style={styles.sectionTitle}>Shipping Notes</Text>
        {shipMethod ? (
          <View style={styles.shippingRow}>
            <Text style={styles.shippingLabel}>Ship Method:</Text>
            <Text style={styles.shippingValue}>{shipMethod}</Text>
          </View>
        ) : null}
        {shipType ? (
          <View style={styles.shippingRow}>
            <Text style={styles.shippingLabel}>Ship Type:</Text>
            <Text style={styles.shippingValue}>{shipType}</Text>
          </View>
        ) : null}
        {trackingNumber ? (
          <View style={styles.shippingRow}>
            <Text style={styles.shippingLabel}>Tracking #:</Text>
            <Text style={styles.shippingValue}>{trackingNumber}</Text>
          </View>
        ) : null}
        {estimatedArrival ? (
          <View style={styles.shippingRow}>
            <Text style={styles.shippingLabel}>Est. Arrival:</Text>
            <Text style={styles.shippingValue}>{estimatedArrival}</Text>
          </View>
        ) : null}
        {shippingNotes ? (
          <View style={styles.shippingRow}>
            <Text style={styles.shippingLabel}>Shipping Notes:</Text>
            <Text style={styles.shippingValue}>{shippingNotes}</Text>
          </View>
        ) : null}
      </View>
    ) : null}
  </View>
);

// ────────────────────────────────────────────────────────────
// POFooter
// ────────────────────────────────────────────────────────────

export const POFooter = () => (
  <View style={styles.footer} fixed>
    <ReachProOutlineLogoPDF width={24} height={24} color="#221e1f" />
  </View>
);
